import {
  buildCollection,
  buildProperties,
  buildProperty,
  EntityReference,
  type CollectionActionsProps,
} from "@firecms/core";
import { canAdminEvent, getEventPath, type EventCollectionId } from "../utils";
import { AddSlotsButton } from "../actions/AddSlotsButton";
import { buildChangeParticipantTeamEntityAction } from "../actions/changeSlotTeam/buildChangeSlotTeamEntityAction";
import { buildViewParticipantEconomyEntityAction } from "../actions/participantEconomy/buildViewParticipantEconomyEntityAction";
import type { AuthExtras } from "../types/auth";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import type { Locale } from "../localization/constants";
import type { Story } from "./stories";
import { buildLevelProgressCollection } from "./levelProgress";
import { buildUnitProgressCollection } from "./unitProgress";
import { buildParticipantActionLogsCollection } from "./actionLogs";
import { NAV_GROUP_SESSION } from "./navigationGroups";
import { buildTeamsPath } from "./teams";
import { buildParticipantLedgerCollection } from "./ledgers";
import { buildParticipantNotificationsCollection } from "./notifications";

export type AssignedParticipant = {
  participantNumber: number;
  teamRef?: EntityReference;
};

export const buildAssignedParticipantsPath = (id: EventCollectionId) =>
  `${getEventPath(id)}/participant`;

/** @deprecated Use buildAssignedParticipantsPath */
export const buildParticipantsPath = buildAssignedParticipantsPath;

export const buildAssignedParticipantsCollection = (
  id: EventCollectionId,
  hideFromNavigation?: boolean,
  locale: Locale = getAdminUiLocale()
) => {
  const t = getAdminT(locale);

  return buildCollection<AssignedParticipant>({
    name: t("assignedParticipants.collectionName"),
    singularName: t("assignedParticipants.singularName"),
    description: t("assignedParticipants.description"),
    path: buildAssignedParticipantsPath(id),
    id: "participants",
    icon: "EmojiPeople",
    group: NAV_GROUP_SESSION,
    hideFromNavigation,
    defaultSize: "s",
    permissions: ({ authController }) => {
      const extra = authController.extra as AuthExtras | undefined;
      const canManage = canAdminEvent(id, extra);
      return {
        read: canManage,
        edit: false,
        create: false,
        delete: false,
      };
    },
    properties: buildProperties<AssignedParticipant>({
      participantNumber: buildProperty({
        name: t("assignedParticipants.fields.number"),
        description: t("assignedParticipants.fields.numberDescription"),
        dataType: "number",
        readOnly: true,
      }),
      teamRef: buildProperty({
        name: t("assignedParticipants.fields.team"),
        description: t("assignedParticipants.fields.teamDescription"),
        dataType: "reference",
        path: buildTeamsPath(id),
        previewProperties: ["teamNumber"],
        readOnly: true,
      }),
    }),
    subcollections: [
      buildLevelProgressCollection(id, locale),
      buildUnitProgressCollection(id, locale),
      buildParticipantActionLogsCollection(id, locale),
      buildParticipantLedgerCollection(id, locale),
      buildParticipantNotificationsCollection(id),
    ],
    initialSort: ["participantNumber", "asc"],
    entityActions: [
      buildViewParticipantEconomyEntityAction(),
      buildChangeParticipantTeamEntityAction(),
    ],
    Actions: ({ selectionController: _selectionController }: CollectionActionsProps<Story>) => [
      <AddSlotsButton key="add-participant-slots" />,
    ],
  });
};

/** @deprecated Use buildAssignedParticipantsCollection */
export const buildParticipantsCollection = buildAssignedParticipantsCollection;

/** @deprecated Use AssignedParticipant */
export type Participant = AssignedParticipant;
